import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import api from '../../services/authService';

const TrendsAnalytics = () => {
  const [trends, setTrends] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchTrends();
    fetchSummary();
  }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTrends = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/trends?days=${period}`);
      setTrends(response.data.trends);
    } catch (err) {
      console.error('Error fetching trends:', err);
      setError('Failed to load trend data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await api.get(`/analytics/historical-summary?period=${period}`);
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setError('Failed to load summary data');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTrendIcon = (current, previous) => {
    if (!previous) return <TrendingFlat />;
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return <TrendingFlat />;
  };

  const getScoreColor = (score) => {
    if (score == null || isNaN(score)) return 'default';
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  if (loading && !trends.length) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">
          Analytics Trends
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={period}
            label="Time Period"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Meetings
                </Typography>
                <Typography variant="h4">
                  {summary.total_meetings || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Last {period} days
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Avg Engagement
                </Typography>
                <Typography variant="h4">
                  {summary.avg_engagement_score ? summary.avg_engagement_score.toFixed(1) : 'N/A'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={
                      summary.min_engagement != null && summary.max_engagement != null
                        ? `${summary.min_engagement.toFixed(0)} - ${summary.max_engagement.toFixed(0)}`
                        : 'N/A'
                    }
                    color={getScoreColor(summary.avg_engagement_score)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Avg Quality
                </Typography>
                <Typography variant="h4">
                  {summary.avg_quality_score ? summary.avg_quality_score.toFixed(1) : 'N/A'}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    size="small"
                    label={
                      summary.min_quality != null && summary.max_quality != null
                        ? `${summary.min_quality.toFixed(0)} - ${summary.max_quality.toFixed(0)}`
                        : 'N/A'
                    }
                    color={getScoreColor(summary.avg_quality_score)}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Participants
                </Typography>
                <Typography variant="h4">
                  {summary.total_participants || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Avg: {summary.avg_participants ? summary.avg_participants.toFixed(1) : 'N/A'} per meeting
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Daily Trends Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Daily Trends
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Day-by-day breakdown of meeting analytics
          </Typography>

          {trends.length === 0 ? (
            <Alert severity="info">
              No meeting data available for the selected period.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Meetings</TableCell>
                    <TableCell align="right">Participants</TableCell>
                    <TableCell align="right">Avg Engagement</TableCell>
                    <TableCell align="right">Avg Quality</TableCell>
                    <TableCell align="right">Avg Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trends.map((trend, index) => {
                    const prevTrend = index > 0 ? trends[index - 1] : null;
                    return (
                      <TableRow key={trend.date}>
                        <TableCell>{formatDate(trend.date)}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
                            {trend.meeting_count}
                            {getTrendIcon(trend.meeting_count, prevTrend?.meeting_count)}
                          </Box>
                        </TableCell>
                        <TableCell align="right">{trend.total_participants || 0}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={trend.avg_engagement ? trend.avg_engagement.toFixed(1) : 'N/A'}
                            color={getScoreColor(trend.avg_engagement)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={trend.avg_quality ? trend.avg_quality.toFixed(1) : 'N/A'}
                            color={getScoreColor(trend.avg_quality)}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {trend.avg_duration ? `${Math.round(trend.avg_duration / 60)}m` : 'N/A'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ mt: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> For visual charts (line graphs, bar charts), consider adding 
                a charting library like Recharts or Chart.js. This view shows tabular trend data.
              </Typography>
            </Alert>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TrendsAnalytics;
